import { useState, useMemo, useEffect } from 'react'
import type { WizardStepProps } from '../../../../types/builder.types'
import { getAllFields } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { TableFieldsModal } from '../TableFieldsModal'

export function TableConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()
  const [showTableFields, setShowTableFields] = useState(false)

  const actualDataSource = config.dataSource === "custom_query" 
  ? config.customQuery?.config?.baseTable 
  : config.dataSource;

  

  const customQueryFieldsKey = useMemo(() => {
    if (config.dataSource !== 'custom_query' || !config.customQuery?.config?.selectedFields)
      return ''
    return JSON.stringify({
      fields: config.customQuery.config.selectedFields,
      aliases: config.customQuery.config.fieldAliases || {},
      agg: config.customQuery.config.agreggation || [],
    })
  }, [
    config.dataSource,
    config.customQuery?.config?.selectedFields,
    config.customQuery?.config?.fieldAliases,
    config.customQuery?.config?.agreggation,
  ])

  // Get fields from dynamic data sources or fallback to static
  const allFields = useMemo(() => {
    if (!config.dataSource) return []

    // First try to find in dynamic sources (includes both ETL and CKAN)
    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return dynamicSource.fields
    }

    if (config.customQuery?.config?.selectedFields) {
      const aliases = config.customQuery.config?.fieldAliases || {}
      return config.customQuery.config.selectedFields.map((f: string) => ({
        name: f,
        label: aliases[f] || f,
        type: 'custom'
      }))
    }

    // Fallback to static function for backwards compatibility
    return getAllFields(config.dataSource)
  }, [dataSources, config.dataSource, customQueryFieldsKey])
  const [selectedColumns, setSelectedColumns] = useState<string[]>(
    config.tableConfig?.columns?.map(c => c.key) || []
  )

  // Si cambia la consulta (QueryBuilder), quitar columnas que ya no existen en el SELECT
  // y alinear la selección con los campos actuales (evita carbono_aereo vacío, etc.).
  useEffect(() => {
    if (config.dataSource !== 'custom_query' || !customQueryFieldsKey) return

    const fieldNames = config.customQuery!.config!.selectedFields!
    const aliases = config.customQuery!.config!.fieldAliases || {}
    const prevKeys = (config.tableConfig?.columns || []).map(c => c.key)
    let nextKeys = prevKeys.filter(k => fieldNames.includes(k))
    const hadStale = prevKeys.some(k => !fieldNames.includes(k))

    if (!hadStale) return

    if (nextKeys.length === 0 && fieldNames.length > 0 && prevKeys.length > 0) {
      nextKeys = [...fieldNames]
    }

    setSelectedColumns(nextKeys)
    onChange({
      ...config,
      tableConfig: {
        ...config.tableConfig,
        columns: nextKeys.map(key => {
          const field = fieldNames.includes(key)
            ? { name: key, label: aliases[key] || key }
            : { name: key, label: key }
          return {
            key,
            label: field.label,
            sortable: true,
          }
        }),
      },
    })
  }, [customQueryFieldsKey])

  const [allowAggregation, setAllowAggregation] = useState<boolean>(false);

  const handleColumnAggregation = (checked: boolean) => {
    setAllowAggregation(checked);
  };

  const handleColumnToggle = (fieldName: string) => {
    const newSelected = selectedColumns.includes(fieldName)
      ? selectedColumns.filter(c => c !== fieldName)
      : [...selectedColumns, fieldName]

    setSelectedColumns(newSelected)

    const columns = newSelected.map(key => {
      const field = allFields.find(f => f.name === key)
      return {
        key,
        label: field?.label || key,
        sortable: true
      }
    })

    onChange({
      ...config,
      tableConfig: {
        ...config.tableConfig,
        columns
      }
    })
  }

  const isValid = Boolean(
    config.metadata?.title &&
    selectedColumns.length > 0
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-6">Configurar Tabla</h2>

      <div className="space-y-6 bg-white border rounded-lg p-6">

        <div>
          {/* Botón o trigger para mostrar los campos */}
          <button
            onClick={() => setShowTableFields(true)}
            disabled={!config.dataSource}
            className="px-6 py-2 text-sm rounded-lg whitespace-nowrap transition-all border bg-blue-600 text-white border-blue-600 shadow-sm"
          >
            Ver campos disponibles para fuente de datos : <b>{actualDataSource}</b>
          </button>

          {/* Modal de campos (incluye fuentes BMP: campos y muestra de registros) */}
           {showTableFields && actualDataSource && (
              <TableFieldsModal
                dataSource={actualDataSource}
                componentType={config.componentType}
                dataSources={dataSources}
                onClose={() => setShowTableFields(false)}
              />
            )}
        </div>
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título de la Tabla <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Listado de Municipios"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Columnas */}
        <div>
          <label className="block font-medium mb-2">
            Columnas a Mostrar <span className="text-red-500">*</span>
          </label>
          <div className="border rounded p-4 max-h-64 overflow-y-auto">
            {allFields.map((field: any) => (
              <label
                key={field.name}
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(field.name)}
                  onChange={() => handleColumnToggle(field.name)}
                  className="w-4 h-4 text-green-600"
                />
                <div className="flex-1">
                  <div className="font-medium">{field.label}</div>
                  {field.description && (
                    <div className="text-xs text-gray-500">{field.description}</div>
                  )}
                </div>
                <span className="text-xs text-gray-400">{field.type}</span>
              </label>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-2">
            Seleccionadas: {selectedColumns.length} columnas
          </p>
        </div>


        {/*Nueva seccion de agrupamiento*/}
        <div>
              <label
                key="permite_agrupamiento"
                className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  onChange={(e) => handleColumnAggregation(e.target.checked)}
                  className="w-4 h-4 text-green-600"
                />
                <div className="flex-1">
                  <div className="font-medium">¿ Permitir agrupamiento ?</div>
                </div>
              </label>
        </div>

        {allowAggregation && (
          <div className="mt-4 p-4 border rounded bg-gray-50">
            {/* Contenido de agrupamiento */}
            <p>Opciones de agrupamiento activadas</p>
          </div>
        )}


        {/*termino Nueva seccion de agrupamiento */}

        {/* Registros por página */}
        <div>
          <label className="block font-medium mb-2">Registros por Página</label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.tableConfig?.pageSize || 10}
            onChange={(e) =>
              onChange({
                ...config,
                tableConfig: {
                  ...config.tableConfig,
                  pageSize: parseInt(e.target.value)
                }
              })
            }
          >
            <option value={10}>10 registros</option>
            <option value={20}>20 registros</option>
            <option value={50}>50 registros</option>
            <option value={100}>100 registros</option>
          </select>
        </div>

        {/* Botón continuar */}
        <button
          onClick={onNext}
          disabled={!isValid}
          className="w-full bg-green-600 text-white py-3 rounded font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed mt-6"
        >
          Continuar a Vista Previa →
        </button>
      </div>
    </div>
  )
}
